import { Box, Button, Card, CardBody, Flex, Heading, HStack, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  MinimumRequirementResponseType,
  type MinimumRequirementDto,
  type SupplierBidMinimumRequirementResponseDto,
} from "../../../../autogen/bff-api";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";
import { MinimumRequirementAttachments } from "./MinimumRequirementAttachments";

// t('Your answer?') t('Yes') t('No') t('Explain your answer') t('Comment (optional)')
// t('Answered') t('Not answered') t('Comment required') t('Documentation required') t('Not met')

interface Props {
  bidId: string;
  requirement: MinimumRequirementDto;
  response: SupplierBidMinimumRequirementResponseDto | undefined;
  isReadOnly?: boolean;
  onAnswerChange: (answer: boolean) => void;
  onCommentChange: (comment: string) => void;
  onAttachmentsChange: (attachmentIds: string[]) => void;
}

export type MinimumRequirementAnswerStatus =
  | "unanswered"
  | "comment-required"
  | "documentation-required"
  | "not-met"
  | "met";

const attachmentIdsOf = (response: SupplierBidMinimumRequirementResponseDto | undefined): string[] =>
  response?.attachmentIds ?? response?.attachments?.map((document) => document.id) ?? [];

// Mirrors BidV2MinimumRequirementCompleteness in the BFF; the two must agree on what "complete" means.
export const isMinimumRequirementResponseComplete = (
  requirement: MinimumRequirementDto,
  response: SupplierBidMinimumRequirementResponseDto | undefined,
): boolean => {
  if (!response) return false;
  if (requirement.responseType === MinimumRequirementResponseType.YesNoComment && response.answer === false) {
    return (response.comment ?? "").trim().length > 0;
  }
  if (requirement.responseType === MinimumRequirementResponseType.YesNoDocument && response.answer === true) {
    return attachmentIdsOf(response).length > 0;
  }
  return true;
};

export const minimumRequirementAnswerStatus = (
  requirement: MinimumRequirementDto,
  response: SupplierBidMinimumRequirementResponseDto | undefined,
): MinimumRequirementAnswerStatus => {
  if (!response) return "unanswered";
  if (!isMinimumRequirementResponseComplete(requirement, response)) {
    return requirement.responseType === MinimumRequirementResponseType.YesNoDocument
      ? "documentation-required"
      : "comment-required";
  }
  return response.answer ? "met" : "not-met";
};

const VARIANT_BY_STATUS: Record<MinimumRequirementAnswerStatus, StatusVariant> = {
  unanswered: "neutral",
  "comment-required": "issue",
  "documentation-required": "issue",
  "not-met": "issue",
  met: "ok",
};

const LABEL_KEY_BY_STATUS: Record<MinimumRequirementAnswerStatus, string> = {
  unanswered: "Not answered",
  "comment-required": "Comment required",
  "documentation-required": "Documentation required",
  "not-met": "Not met",
  met: "Answered",
};

export const MinimumRequirementAnswerCard = ({
  bidId,
  requirement,
  response,
  isReadOnly,
  onAnswerChange,
  onCommentChange,
  onAttachmentsChange,
}: Props) => {
  const { t } = useTranslation();
  const status = minimumRequirementAnswerStatus(requirement, response);
  const variant = VARIANT_BY_STATUS[status];
  const acceptsComment = requirement.responseType === MinimumRequirementResponseType.YesNoComment;
  const commentIsRequired = acceptsComment && response?.answer === false;
  const asksForDocument = requirement.responseType === MinimumRequirementResponseType.YesNoDocument;

  return (
    <Card variant="outline" rounded="xl" position="relative" overflow="hidden">
      <StatusAccentBar variant={variant} />
      <CardBody>
        <Heading size="sm" mb="1">
          {requirement.title}
        </Heading>
        {!isTiptapContentEmpty(requirement.description) && (
          <Box fontSize="sm" color="smMuted" mb="4">
            <TipTap content={requirement.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
          </Box>
        )}

        <Box bg="smBackgroundSecondary" borderRadius="md" p="4">
          <Flex mb="2" justify="space-between" align="center" gap="2">
            <Text fontSize="sm" fontWeight="medium">
              {t("Your answer?")}
            </Text>
            <StatusDotLabel variant={variant} label={t(LABEL_KEY_BY_STATUS[status])} />
          </Flex>

          <HStack spacing="2">
            <Button
              size="sm"
              variant={response?.answer === true ? "solid" : "outline"}
              colorScheme="teal"
              isDisabled={isReadOnly}
              onClick={() => onAnswerChange(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              size="sm"
              variant={response?.answer === false ? "solid" : "outline"}
              colorScheme={response?.answer === false ? "orange" : "teal"}
              isDisabled={isReadOnly}
              onClick={() => onAnswerChange(false)}
            >
              {t("No")}
            </Button>
          </HStack>

          {acceptsComment && response && (
            <Textarea
              mt="3"
              rows={3}
              placeholder={String(commentIsRequired ? t("Explain your answer") : t("Comment (optional)"))}
              isReadOnly={isReadOnly}
              value={response.comment ?? ""}
              onChange={(e) => onCommentChange(e.target.value)}
            />
          )}

          {asksForDocument && response?.answer === true && (
            <MinimumRequirementAttachments
              bidId={bidId}
              minimumRequirementId={requirement.id ?? ""}
              documents={response.attachments ?? []}
              attachmentIds={attachmentIdsOf(response)}
              isReadOnly={isReadOnly ?? false}
              onAttachmentsChange={onAttachmentsChange}
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
};
